import React from 'react';
import { Link, NavLink, useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { LayoutDashboard, FilePlus2, FolderOpen, LogOut } from 'lucide-react';
import { useNominatorAuth } from '@/context/NominatorAuthContext';

// Portal shell — gated by NominatorAuthContext. If unauthenticated, we hand
// off to /portal/login rather than showing a spinner-forever page.
const PortalLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nominator, loading, logout } = useNominatorAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-soft)] text-[11px] tracking-[0.28em] uppercase">
        Checking your session…
      </div>
    );
  }
  // Use <Navigate/> (declarative) instead of imperative navigate() during
  // render. Imperative navigate during render causes React 18 to bail out
  // with a blank page on production builds where StrictMode double-renders.
  if (!nominator) {
    return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  }

  const signOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const initials = (nominator.name || 'E').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)] flex" data-testid="portal-shell">
      <aside className="hidden md:flex flex-col w-64 border-r border-[#B8884A]/30 bg-[#4E3B2A] text-white">
        <div className="p-7 border-b border-[#B8884A]/25">
          <div className="text-[10px] tracking-[0.5em] uppercase text-[#D9B26A]">AdAura</div>
          <div className="font-serif text-xl text-white mt-1">Nomination Portal</div>
        </div>
        <nav className="flex-1 p-5 space-y-1">
          <RailLink to="/portal/dashboard" icon={LayoutDashboard} label="Dashboard" />
          <RailLink to="/portal/new" icon={FilePlus2} label="New Nomination" />
          <RailLink to="/portal/entries" icon={FolderOpen} label="My Entries" />
        </nav>
        <div className="p-5 border-t border-[#B8884A]/25">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 inline-flex items-center justify-center bg-[#D9B26A] text-[#4E3B2A] font-serif font-bold text-sm">{initials}</span>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm truncate" data-testid="portal-user-name">{nominator.name || 'Entrant'}</div>
              <div className="text-white/55 text-[10px] truncate">{nominator.email}</div>
              {nominator.organisation && <div className="text-white/40 text-[10px] truncate mt-0.5" data-testid="portal-user-company">{nominator.organisation}</div>}
            </div>
          </div>
          <button onClick={signOut} data-testid="portal-signout" className="mt-4 inline-flex items-center gap-2 text-white/55 hover:text-[#D9B26A] text-[10px] tracking-[0.3em] uppercase transition-colors">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-30 border-b border-[#B8884A]/25 bg-[#4E3B2A]">
        <div className="px-5 py-3 flex items-center justify-between">
          <Link to="/portal/dashboard">
            <div className="text-[9px] tracking-[0.5em] uppercase text-[#D9B26A]">AdAura</div>
            <div className="font-serif text-base text-white">Nomination Portal</div>
          </Link>
          <button onClick={signOut} aria-label="Sign out" className="text-white/65"><LogOut size={16} /></button>
        </div>
        <div className="px-5 pb-2 flex gap-4 text-[10px] tracking-[0.3em] uppercase">
          <NavLink to="/portal/dashboard" className={({ isActive }) => isActive ? 'text-[#D9B26A] border-b-2 border-[#D9B26A] pb-1' : 'text-white/55 pb-1'}>Dashboard</NavLink>
          <NavLink to="/portal/new" className={({ isActive }) => isActive ? 'text-[#D9B26A] border-b-2 border-[#D9B26A] pb-1' : 'text-white/55 pb-1'}>New</NavLink>
          <NavLink to="/portal/entries" className={({ isActive }) => isActive ? 'text-[#D9B26A] border-b-2 border-[#D9B26A] pb-1' : 'text-white/55 pb-1'}>Entries</NavLink>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-28 md:pt-10 pb-12">
        <div className="max-w-6xl mx-auto px-5 md:px-10" key={location.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const RailLink = ({ to, icon: Icon, label }) => (
  <NavLink to={to} className={({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-xs tracking-[0.25em] uppercase transition-colors ${isActive ? 'bg-[#D9B26A]/15 text-[#D9B26A] border-l-2 border-[#D9B26A]' : 'text-white/65 hover:text-white border-l-2 border-transparent'}`
  } data-testid={`portal-nav-${label.toLowerCase().split(' ')[0]}`}>
    <Icon size={14} /> {label}
  </NavLink>
);

export default PortalLayout;
